import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import SEOHead from '../../../components/feature/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const industrialSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Industrial Cleaning Services',
  description: 'Heavy-duty industrial cleaning services in Louisville, KY for warehouses, manufacturing plants, and production facilities. OSHA-compliant, flexible scheduling.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'First Insight Cleaning',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Louisville',
      addressRegion: 'KY',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Louisville' },
  serviceType: 'Industrial Cleaning',
};

export default function IndustrialCleaningPage() {
  const [scrolled, setScrolled] = useState(true);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 10); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Industrial Cleaning Services Louisville KY | First Insight Cleaning"
        description="Heavy-duty industrial cleaning in Louisville, KY. Warehouse floor maintenance, equipment cleaning, OSHA-compliant safety protocols &amp; flexible scheduling for manufacturing facilities. Get a free quote."
        keywords="industrial cleaning Louisville KY, warehouse cleaning Kentucky, manufacturing facility janitorial, First Insight Cleaning"
        canonical={`${siteUrl}/services/industrial-cleaning`}
        schemaJson={industrialSchema}
      />
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b9fbe279b6f73464e98e003668a4de5e.jpg"
            alt="Industrial Cleaning Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">Industrial Cleaning Services</h1>
          <p className="text-base md:text-xl max-w-2xl mx-auto">Heavy-duty cleaning solutions for manufacturing and industrial facilities</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-800 mb-6">Specialized Industrial Facility Maintenance</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              First Insight Cleaning delivers comprehensive industrial cleaning services designed to meet the demanding requirements of manufacturing plants, warehouses, and production facilities. Our experienced team understands the unique challenges of industrial environments and employs specialized equipment and techniques to maintain safe, compliant, and efficient workspaces.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              We work around your production schedules to minimize disruption while ensuring thorough cleaning of all areas, from production floors to equipment surfaces. Our industrial cleaning protocols address heavy machinery cleaning, high-dust environments, and specialized surface treatments required in manufacturing settings.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Heavy Equipment Cleaning</h3>
              <p className="text-slate-600">Specialized cleaning for machinery, production equipment, and industrial tools using appropriate methods and solutions.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Warehouse Floor Maintenance</h3>
              <p className="text-slate-600">Deep cleaning and maintenance of concrete floors, loading docks, and high-traffic industrial areas.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Safety Compliance Cleaning</h3>
              <p className="text-slate-600">OSHA-compliant cleaning procedures ensuring workplace safety standards and regulatory requirements are met.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-delete-bin-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Industrial Waste Management</h3>
              <p className="text-slate-600">Proper handling and disposal of industrial waste materials following environmental regulations.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-windy-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">High-Dust Removal</h3>
              <p className="text-slate-600">Specialized equipment for removing accumulated dust from high ceilings, beams, and overhead structures.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Flexible Scheduling</h3>
              <p className="text-slate-600">Off-hours and weekend cleaning services to avoid disruption to your production operations.</p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">Why Choose Our Industrial Cleaning</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Experienced team trained in industrial safety protocols</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Commercial-grade equipment for heavy-duty cleaning</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Compliance with OSHA and environmental regulations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Customized cleaning plans for your facility type</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Minimal disruption to production schedules</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Quality assurance inspections after every service</span>
                </li>
              </ul>
            </div>
            <div className="h-[400px]">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_444b74fe063483f5424afc24d2d3085b_6840aa99.jpg"
                alt="Industrial Cleaning in Action"
                className="w-full h-full object-cover rounded-lg shadow-lg object-top"
              />
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-teal-500 rounded-lg p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Maintain Your Industrial Facility?</h2>
            <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
              Get a customized cleaning plan tailored to your industrial operations
            </p>
            <Link 
              to="/#quote"
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
